import { defineStore } from 'pinia'

/**
 * Store del POS. Mismo patron que `store/quick-booking.js` (pinia, options API),
 * pero en un store aparte: el flujo de reserva no se toca.
 *
 * REGLA DE ORO DE ESTE STORE: los getters de totales son una ESTIMACION para
 * pintar sumas en vivo. La verdad de dinero y de IVA la calcula el servidor
 * (`PosController::previewTotals` recalcula precios desde la BD y nunca confia
 * en el cliente). Por eso:
 *   - lo que aqui se puede replicar con exactitud (precios de catalogo,
 *     descuento de producto, IVA) se replica con la MISMA formula del backend;
 *   - lo que NO se puede saber sin consultar (cupon, puntos->dinero, saldo de
 *     monedero, saldo de gift card) NO se inventa: se marca como "lo resuelve el
 *     servidor" y solo se muestra con los importes reales de la respuesta.
 */

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100

/**
 * Normaliza un id que viene de un <select>. Ojo: `<option :value="null">` llega
 * como la CADENA "null", y `Number('null')` es NaN. Sin este guard, un NaN se
 * colaria en el estado y en el payload (JSON.stringify(NaN) === "null"),
 * disparando un 422 confuso. Todo lo que no sea un id positivo -> null.
 */
const toId = (value) => {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}

let uidSeq = 0
const nextUid = () => `line-${++uidSeq}`

const emptyCatalog = () => ({
  branch: null,
  services: [],
  products: [],
  taxes: { services: [], products: [] },
  cash_register: null
})

export const usePos = defineStore('pos', {
  state: () => ({
    branchId: null,
    branches: [],
    catalog: emptyCatalog(),

    customer: null,

    // ORDEN DE TRABAJO en curso (la cita que se esta cobrando). Con esto
    // pos/sale NO crea una reserva nueva: cobra la que ya existe, a la que el
    // staff le fue sumando consumos desde el check-in.
    booking_id: null,

    // Lineas del carrito
    services: [],
    products: [],

    // DESCUENTOS (reducen base imponible)
    coupon_code: '',
    // Descuento manual del cajero: '' | 'percent' | 'fixed' + valor.
    discount_type: '',
    discount_value: null,

    // TENDERS / medios de pago (no reducen base imponible)
    use_points: null,
    use_wallet: null,
    gift_card_code: '',

    // Entrega: 'pickup' (retiro) | 'delivery' (envio -> crea orden con direccion)
    delivery_type: 'pickup',
    shipping_address_id: null,

    // COBRO — modelo UNIFICADO: una lista de lineas de pago. 1 linea = pago
    // simple; 2+ = pago multiple (split). Cada linea:
    //   { method, amount, reference, received }
    //   - amount: importe aplicado a la venta (en 1 linea es el total, implicito).
    //   - reference: referencia de pasarela (medios online; en Datafast es el
    //     resourcePath del widget).
    //   - received: efectivo entregado (solo para el cambio; el backend no lo usa).
    payment_lines: [{ method: 'cash', amount: null, reference: '', received: null, card_brand: '', card_last4: '', auth_code: '', batch: '' }],
    tip: null,
    // Toda venta emite factura SRI (obligatorio); el checkbox se elimino.
    invoice: true,
    note: '',

    // Respuesta REAL del backend (unica fuente de "pagado")
    sale: null,

    // Estado de UI
    loadingCatalog: false,
    submitting: false,
    error: null,
    errorList: []
  }),

  getters: {
    hasLines: (state) => state.services.length > 0 || state.products.length > 0,

    servicesSubtotal: (state) => round2(state.services.reduce((sum, line) => sum + (Number(line.price) || 0), 0)),

    productsSubtotal: (state) =>
      round2(
        state.products.reduce((sum, line) => {
          return sum + (Number(line.unit_price) || 0) * (Number(line.qty) || 0) - (Number(line.line_discount) || 0)
        }, 0)
      ),

    subtotal() {
      return round2(this.servicesSubtotal + this.productsSubtotal)
    },

    /**
     * Descuento MANUAL estimado, con la MISMA formula del backend
     * (`previewTotals`): `percent` = % del subtotal (tope 100%); `fixed` = importe.
     * Nunca mayor que el subtotal. A diferencia del cupon, el front lo conoce con
     * exactitud, asi que SI reduce la base estimada.
     */
    manualDiscountAmount(state) {
      const value = round2(state.discount_value)
      if (value <= 0) return 0

      if (state.discount_type === 'percent') {
        return round2((this.subtotal * Math.min(value, 100)) / 100)
      }
      if (state.discount_type === 'fixed') {
        return round2(Math.min(value, this.subtotal))
      }
      return 0
    },

    /** Base imponible estimada = subtotal - descuento manual (el cupon es del servidor). */
    taxableBaseEstimate() {
      return round2(this.subtotal - this.manualDiscountAmount)
    },

    /**
     * IVA estimado. Se usa el juego de impuestos de `services` (no es un atajo:
     * `PosController::previewTotals` aplica `taxesFor('services')` a TODA la
     * venta, porque ComprobanteService toma la primera tarifa `percent` y la
     * aplica a todos los detalles). Base = subtotal - descuento manual (el cupon
     * lo valora el servidor, asi que aqui el IVA estimado es un techo).
     */
    taxAmount(state) {
      const taxes = state.catalog?.taxes?.services || []
      const base = this.taxableBaseEstimate

      const amount = taxes.reduce((sum, tax) => {
        if (tax.type === 'percent') {
          return sum + (base * (Number(tax.percent) || 0)) / 100
        }
        return sum + (Number(tax.tax_amount) || 0)
      }, 0)

      return round2(amount)
    },

    tipAmount: (state) => round2(state.tip),

    /** Total estimado ANTES de cupon y de cualquier tender (ya con descuento manual). */
    estimatedTotal() {
      return round2(this.taxableBaseEstimate + this.taxAmount + this.tipAmount)
    },

    /** True si el cajero declaro algo que solo el servidor puede valorar. */
    hasServerResolvedAmounts: (state) =>
      !!state.coupon_code || !!state.gift_card_code || Number(state.use_points) > 0 || Number(state.use_wallet) > 0,

    /** Pago simple = 1 linea. */
    isSinglePayment: (state) => state.payment_lines.length === 1,

    /** Pago multiple (split) = 2+ lineas. */
    isMultiPayment: (state) => state.payment_lines.length >= 2,

    /** Metodo efectivo: en 1 linea su metodo; en 2+ el DOMINANTE (mayor importe). */
    effectiveMethod(state) {
      const lines = state.payment_lines
      if (lines.length <= 1) return lines[0]?.method || 'cash'
      return [...lines].sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0))[0]?.method || 'cash'
    },

    /** True si el metodo efectivo es online (exige referencia/verify). */
    effectiveMethodIsOnline() {
      return !['cash', 'transfer', 'cheque'].includes(this.effectiveMethod)
    },

    /** Cubierto por las lineas. En pago simple = el total (importe implicito). */
    paymentsCovered(state) {
      if (state.payment_lines.length <= 1) return this.estimatedTotal
      return round2(state.payment_lines.reduce((sum, p) => sum + (Number(p.amount) || 0), 0))
    },

    /** Lo que falta (o sobra, si negativo) para cuadrar con el total. */
    paymentsRemaining() {
      return round2(this.estimatedTotal - this.paymentsCovered)
    },

    /** Cuadra exacto (tolerancia 1 centavo). En pago simple siempre cuadra. */
    paymentsBalanced() {
      return Math.abs(this.paymentsRemaining) <= 0.01
    },

    /** Cambio = efectivo recibido (lineas cash) - efectivo aplicado. */
    changeDue(state) {
      const single = state.payment_lines.length <= 1
      let received = 0
      let applied = 0
      state.payment_lines.forEach((p) => {
        if (p.method !== 'cash') return
        received += Number(p.received) || 0
        applied += single ? this.estimatedTotal : (Number(p.amount) || 0)
      })
      return round2(Math.max(0, received - applied))
    },

    /** Falta efectivo: se declaro recibido en cash pero no cubre lo aplicado. */
    cashShortfall(state) {
      const single = state.payment_lines.length <= 1
      let received = 0
      let applied = 0
      let anyReceived = false
      state.payment_lines.forEach((p) => {
        if (p.method !== 'cash') return
        if (Number(p.received) > 0) anyReceived = true
        received += Number(p.received) || 0
        applied += single ? this.estimatedTotal : (Number(p.amount) || 0)
      })
      if (!anyReceived) return 0
      return round2(Math.max(0, applied - received))
    },

    /** Linea Datafast en pago simple (para mostrar el widget). */
    isDatafastSingle: (state) => state.payment_lines.length === 1 && state.payment_lines[0]?.method === 'datafast',

    /** Referencia del cobro Datafast (resourcePath), si existe. */
    datafastReference: (state) => {
      const line = state.payment_lines.find((p) => p.method === 'datafast')
      return line ? (line.reference || '') : ''
    }
  },

  actions: {
    setBranch(branchId) {
      this.branchId = toId(branchId)
    },

    setBranches(list) {
      this.branches = Array.isArray(list) ? list : []
    },

    setCatalog(payload) {
      this.catalog = payload || emptyCatalog()
    },

    setCustomer(customer) {
      this.customer = customer
    },

    /**
     * Descuento de linea de producto, con la MISMA semantica que
     * `PosController::productLineDiscount`:
     *   - `variation.discount` viene del catalogo ya calculado para qty=1 y vale
     *     0 cuando la ventana de fechas del descuento NO esta vigente. Por eso
     *     0 en qty=1 => 0 para cualquier qty (no hay descuento activo).
     *   - `percent` escala con la cantidad; `fixed` NO escala (es un tope por
     *     linea: min(discount_value, total de linea)).
     * Es una estimacion para la UI; el importe que manda es el del servidor.
     */
    lineDiscount(product, variation, qty) {
      const unitDiscount = Number(variation.discount) || 0
      if (unitDiscount <= 0) return 0

      const lineTotal = (Number(variation.price) || 0) * qty

      if (product.discount_type === 'percent') {
        return round2((lineTotal * (Number(product.discount_value) || 0)) / 100)
      }

      if (product.discount_type === 'fixed') {
        return round2(Math.min(Number(product.discount_value) || 0, lineTotal))
      }

      return 0
    },

    /**
     * Vuelca una orden de trabajo YA DESCARGADA al carrito.
     *
     * El detalle llega por parametro a proposito: este store es solo estado, la
     * red vive en los componentes (mismo criterio que el resto del POS web).
     *
     * El carrito se REEMPLAZA, no se mezcla: sumar la orden a lo que ya hubiera
     * tecleado el cajero cobraria cosas que nadie pidio.
     */
    applyOrder(detail) {
      if (!detail) return

      this.services = []
      this.products = []
      this.booking_id = Number(detail.booking_id) || null
      this.customer = detail.customer || null

      ;(detail.services || []).forEach((line) => {
        const svc = (this.catalog.services || []).find((x) => Number(x.id) === Number(line.service_id))
        // Un servicio que ya no esta en el catalogo de la sucursal no se puede
        // cobrar (el backend lo revalida): mejor omitirlo que meterlo a 0.
        if (!svc) return
        this.addService(svc)
        const added = this.services[this.services.length - 1]
        if (line.employee_id) added.employee_id = Number(line.employee_id)
      })

      ;(detail.products || []).forEach((line) => {
        ;(this.catalog.products || []).some((p) => {
          const v = (p.variations || []).find(
            (x) => Number(x.product_variation_id) === Number(line.product_variation_id)
          )
          if (!v) return false
          for (let i = 0; i < (Number(line.qty) || 1); i++) this.addProduct(p, v)
          return true
        })
      })
    },

    addService(service) {
      this.services.push({
        uid: nextUid(),
        service_id: Number(service.id),
        name: service.name,
        price: round2(service.price),
        duration_min: Number(service.duration_min) || 0,
        employee_id: null,
        start_date_time: '',
        employees: [],
        // `employeesLoaded` distingue "aun no consultado" de "consultado y sin
        // empleados". Sin el, una lista vacia legitima se re-consultaria en cada
        // cambio del carrito.
        employeesLoaded: false,
        loadingEmployees: false
      })
    },

    addProduct(product, variation) {
      // Misma variacion ya en carrito -> se incrementa (respetando stock).
      const existing = this.products.find((line) => line.product_variation_id === Number(variation.product_variation_id))

      if (existing) {
        this.setProductQty(existing.uid, existing.qty + 1)
        return
      }

      if ((Number(variation.stock) || 0) < 1) return

      this.products.push({
        uid: nextUid(),
        product_id: Number(product.id),
        product_variation_id: Number(variation.product_variation_id),
        name: product.name,
        variation_name: variation.name,
        unit_price: round2(variation.price),
        qty: 1,
        stock: Number(variation.stock) || 0,
        discount_type: product.discount_type,
        discount_value: Number(product.discount_value) || 0,
        unit_discount: Number(variation.discount) || 0,
        // Descuento MANUAL del cajero por linea (override del preset del producto).
        // null = usa el preset. El backend lo recibe como line_discount_*.
        manual_discount_type: null,
        manual_discount_value: null,
        line_discount: this.lineDiscount(product, variation, 1)
      })
    },

    setProductQty(uid, qty) {
      const line = this.products.find((item) => item.uid === uid)
      if (!line) return

      const next = Math.max(1, Math.min(Number(qty) || 1, line.stock))
      line.qty = next
      this.recomputeLine(line)
    },

    /**
     * Recalcula `line_discount` de una linea: si hay descuento MANUAL valido usa
     * ese (misma formula que el backend: percent = % del total, tope 100%; fixed =
     * tope del total de linea); si no, el preset date-gated del producto.
     */
    recomputeLine(line) {
      const lineTotal = (Number(line.unit_price) || 0) * (Number(line.qty) || 0)
      const mt = line.manual_discount_type
      const mv = Number(line.manual_discount_value) || 0

      if ((mt === 'percent' || mt === 'fixed') && mv > 0) {
        line.line_discount = mt === 'percent'
          ? round2((lineTotal * Math.min(mv, 100)) / 100)
          : round2(Math.min(mv, lineTotal))
        return
      }

      line.line_discount = this.lineDiscount(
        { discount_type: line.discount_type, discount_value: line.discount_value },
        { price: line.unit_price, discount: line.unit_discount },
        line.qty
      )
    },

    /** Fija el tipo del descuento manual de una linea (toggle). */
    setLineDiscountType(uid, type) {
      const line = this.products.find((item) => item.uid === uid)
      if (!line) return
      line.manual_discount_type = line.manual_discount_type === type ? null : type
      if (!line.manual_discount_type) line.manual_discount_value = null
      this.recomputeLine(line)
    },

    setLineDiscountValue(uid, value) {
      const line = this.products.find((item) => item.uid === uid)
      if (!line) return
      line.manual_discount_value = value === '' || value === null ? null : Number(value)
      this.recomputeLine(line)
    },

    clearLineDiscount(uid) {
      const line = this.products.find((item) => item.uid === uid)
      if (!line) return
      line.manual_discount_type = null
      line.manual_discount_value = null
      this.recomputeLine(line)
    },

    setServiceEmployee(uid, employeeId) {
      const line = this.services.find((item) => item.uid === uid)
      if (line) line.employee_id = toId(employeeId)
    },

    setServiceStart(uid, startDateTime) {
      const line = this.services.find((item) => item.uid === uid)
      if (line) line.start_date_time = startDateTime
    },

    setServiceEmployees(uid, employees) {
      const line = this.services.find((item) => item.uid === uid)
      if (!line) return

      line.employees = Array.isArray(employees) ? employees : []
      line.employeesLoaded = true
    },

    setServiceLoading(uid, value) {
      const line = this.services.find((item) => item.uid === uid)
      if (line) line.loadingEmployees = value
    },

    removeService(uid) {
      this.services = this.services.filter((line) => line.uid !== uid)
    },

    removeProduct(uid) {
      this.products = this.products.filter((line) => line.uid !== uid)
    },

    /** Vacia el carrito (lineas + nota + pago) sin soltar cliente ni sucursal. */
    clearCart() {
      this.services = []
      this.products = []
      this.note = ''
      this.resetPayments()
      this.clearError()
    },

    // --- COBRO (lineas de pago unificadas) ---
    /** Deja una sola linea de efectivo (estado inicial de cobro). */
    resetPayments() {
      this.payment_lines = [{ method: 'cash', amount: null, reference: '', received: null, card_brand: '', card_last4: '', auth_code: '', batch: '' }]
    },

    /** Anade una linea. Al pasar de 1 a 2, fija el importe de la primera al total
     *  y deja la nueva en 0 para repartir. */
    addPaymentLine(method = 'cash') {
      if (this.payment_lines.length === 1) {
        this.payment_lines[0].amount = this.estimatedTotal
      }
      this.payment_lines.push({ method, amount: 0, reference: '', received: null, card_brand: '', card_last4: '', auth_code: '', batch: '' })
    },

    removePaymentLine(index) {
      if (this.payment_lines.length <= 1) return
      this.payment_lines.splice(index, 1)
      if (this.payment_lines.length === 1) this.payment_lines[0].amount = null
    },

    /** Pone en la ULTIMA linea lo que falte para cuadrar con el total. */
    fillLastLine() {
      if (this.payment_lines.length <= 1) return
      const last = this.payment_lines.length - 1
      const others = this.payment_lines.reduce(
        (sum, p, i) => (i === last ? sum : sum + (Number(p.amount) || 0)),
        0
      )
      this.payment_lines[last].amount = round2(Math.max(0, this.estimatedTotal - others))
    },

    /** Fija la referencia Datafast (resourcePath del retorno del widget). */
    setDatafastReference(reference) {
      const line = this.payment_lines.find((p) => p.method === 'datafast')
      if (!line) {
        this.payment_lines = [{ method: 'datafast', amount: null, reference: reference || '', received: null }]
      } else {
        line.reference = reference || ''
      }
    },

    setError(message, list = []) {
      this.error = message || null
      this.errorList = list || []
    },

    clearError() {
      this.error = null
      this.errorList = []
    },

    setSale(payload) {
      this.sale = payload
    },

    /**
     * Payload de `POST pos/sale`. Solo se mandan IDs y cantidades: los precios
     * los recalcula el servidor desde la BD (dinero + fiscal), asi que enviarlos
     * seria ruido. Las claves opcionales vacias se OMITEN para no chocar con las
     * reglas `nullable|min:1` del validador.
     */
    salePayload() {
      const payload = {
        branch_id: this.branchId,
        user_id: this.customer?.id,
        payment_method: this.effectiveMethod,
        invoice: true // toda venta emite SRI (obligatorio); el motor de reenvio la lleva hasta autorizada
      }

      // Orden de trabajo: cobra la cita existente en vez de crear otra.
      if (Number(this.booking_id) > 0) payload.booking_id = Number(this.booking_id)

      if (this.note) payload.note = this.note

      if (this.services.length) {
        payload.services = this.services.map((line) => {
          const item = {
            service_id: line.service_id,
            employee_id: line.employee_id
          }
          if (line.start_date_time) item.start_date_time = line.start_date_time.replace('T', ' ')
          return item
        })
      }

      if (this.products.length) {
        payload.products = this.products.map((line) => {
          const item = {
            product_variation_id: line.product_variation_id,
            product_qty: line.qty
          }
          // Descuento manual por linea (override del preset): solo si es valido.
          if (['percent', 'fixed'].includes(line.manual_discount_type) && Number(line.manual_discount_value) > 0) {
            item.line_discount_type = line.manual_discount_type
            item.line_discount_value = Number(line.manual_discount_value)
          }
          return item
        })
      }

      if (this.coupon_code) payload.coupon_code = this.coupon_code
      // Descuento manual: solo si hay tipo valido y valor > 0.
      if (['percent', 'fixed'].includes(this.discount_type) && Number(this.discount_value) > 0) {
        payload.discount_type = this.discount_type
        payload.discount_value = Number(this.discount_value)
      }
      if (Number(this.use_points) > 0) payload.use_points = Number(this.use_points)
      if (Number(this.use_wallet) > 0) payload.use_wallet = Number(this.use_wallet)
      if (this.gift_card_code) payload.gift_card_code = this.gift_card_code
      if (Number(this.tip) > 0) payload.tip = Number(this.tip)
      // COBRO unificado: 1 linea -> pago simple (referencia solo si es online);
      // 2+ lineas -> pago multiple (payments[]). El backend usa el metodo dominante
      // para transaction_type/forma SRI cuando llega payments[].
      if (this.isMultiPayment) {
        payload.payments = this.payment_lines
          .filter((p) => Number(p.amount) > 0)
          .map((p) => {
            const item = { method: p.method, amount: round2(p.amount) }
            if (p.reference && String(p.reference).trim() !== '') {
              item.reference = String(p.reference).trim()
            }
            return item
          })
      } else {
        const line = this.payment_lines[0] || {}
        if (this.effectiveMethodIsOnline && line.reference && String(line.reference).trim() !== '') {
          payload.payment_reference = String(line.reference).trim()
        }

        // Voucher del datafono (tarjeta con captura manual). Solo se mandan las
        // claves con valor: el backend usa filled() y una cadena vacia se
        // guardaria como voucher en blanco.
        ;['card_brand', 'card_last4', 'auth_code', 'batch'].forEach((k) => {
          const v = line[k]
          if (v && String(v).trim() !== '') payload[k] = String(v).trim()
        })
      }

      // Entrega a domicilio: solo si hay productos y una direccion elegida.
      if (this.delivery_type === 'delivery' && this.products.length && Number(this.shipping_address_id) > 0) {
        payload.delivery_type = 'delivery'
        payload.shipping_address_id = Number(this.shipping_address_id)
      }

      return payload
    },

    /**
     * Persiste el carrito en sessionStorage antes de saltar al widget de Datafast
     * (que redirige y perderia el estado del SPA). Se restaura al volver.
     */
    saveDraft() {
      try {
        const draft = {
          branchId: this.branchId,
          customer: this.customer,
          services: this.services,
          products: this.products,
          coupon_code: this.coupon_code,
          discount_type: this.discount_type,
          discount_value: this.discount_value,
          use_points: this.use_points,
          use_wallet: this.use_wallet,
          gift_card_code: this.gift_card_code,
          tip: this.tip,
          invoice: this.invoice,
          note: this.note,
          delivery_type: this.delivery_type,
          shipping_address_id: this.shipping_address_id,
          payment_lines: this.payment_lines
        }
        sessionStorage.setItem('pos_draft', JSON.stringify(draft))
      } catch (e) {
        // Sin sessionStorage el flujo Datafast simplemente no persiste el carrito.
      }
    },

    /** Restaura el carrito guardado (al volver del widget). Devuelve el branchId o null. */
    restoreDraft() {
      try {
        const raw = sessionStorage.getItem('pos_draft')
        if (!raw) return null
        sessionStorage.removeItem('pos_draft')

        const d = JSON.parse(raw)
        this.customer = d.customer ?? null
        this.services = Array.isArray(d.services) ? d.services : []
        this.products = Array.isArray(d.products) ? d.products : []
        this.coupon_code = d.coupon_code ?? ''
        this.discount_type = d.discount_type ?? ''
        this.discount_value = d.discount_value ?? null
        this.use_points = d.use_points ?? null
        this.use_wallet = d.use_wallet ?? null
        this.gift_card_code = d.gift_card_code ?? ''
        this.tip = d.tip ?? null
        this.invoice = !!d.invoice
        this.note = d.note ?? ''
        this.delivery_type = d.delivery_type ?? 'pickup'
        this.shipping_address_id = d.shipping_address_id ?? null
        this.payment_lines = Array.isArray(d.payment_lines) && d.payment_lines.length
          ? d.payment_lines
          : [{ method: 'datafast', amount: null, reference: '', received: null }]

        return toId(d.branchId)
      } catch (e) {
        return null
      }
    },

    /** Foto del carrito (para ordenes en espera). No incluye cobro ni sucursal. */
    snapshotCart() {
      return {
        customer: this.customer,
        services: this.services,
        products: this.products,
        coupon_code: this.coupon_code,
        discount_type: this.discount_type,
        discount_value: this.discount_value,
        use_points: this.use_points,
        use_wallet: this.use_wallet,
        gift_card_code: this.gift_card_code,
        tip: this.tip,
        note: this.note,
        delivery_type: this.delivery_type,
        shipping_address_id: this.shipping_address_id
      }
    },

    /** Restaura una foto de carrito (al retomar una orden en espera). */
    restoreCart(s) {
      if (!s) return
      this.customer = s.customer ?? null
      this.services = Array.isArray(s.services) ? s.services : []
      this.products = Array.isArray(s.products) ? s.products : []
      this.coupon_code = s.coupon_code ?? ''
      this.discount_type = s.discount_type ?? ''
      this.discount_value = s.discount_value ?? null
      this.use_points = s.use_points ?? null
      this.use_wallet = s.use_wallet ?? null
      this.gift_card_code = s.gift_card_code ?? ''
      this.tip = s.tip ?? null
      this.note = s.note ?? ''
      this.delivery_type = s.delivery_type ?? 'pickup'
      this.shipping_address_id = s.shipping_address_id ?? null
      this.clearError()
    },

    /** Limpia la venta pero conserva sucursal y catalogo (siguiente cliente). */
    resetSale() {
      this.services = []
      this.products = []
      this.customer = null
      this.booking_id = null
      this.coupon_code = ''
      this.discount_type = ''
      this.discount_value = null
      this.use_points = null
      this.use_wallet = null
      this.gift_card_code = ''
      this.delivery_type = 'pickup'
      this.shipping_address_id = null
      this.resetPayments()
      this.tip = null
      this.invoice = true
      this.note = ''
      this.sale = null
      this.submitting = false
      this.clearError()
    }
  }
})
